import React, { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { DefectReason, deleteDefectReason, errorMessage, fetchDefectReasons, reorderDefectReasons, saveDefectReason } from './packingApi';
import { btn, input, td, th, visuallyHidden } from './packingStyles';

interface Props {
  onMessage: (text: string, error?: boolean) => void;
}

/**
 * "Rodzaje wad" (manager): the list the packing station picks a defect from. Inactive reasons stay in
 * the records but are no longer offered.
 */
const PackingDefectReasonsView: React.FC<Props> = ({ onMessage }) => {
  const { t } = useLanguage();
  const [reasons, setReasons] = useState<DefectReason[]>([]);
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<{ id: number; name: string } | null>(null);

  const load = useCallback(async () => {
    try {
      setReasons(await fetchDefectReasons());
    } catch (e: any) {
      onMessage(errorMessage(e, t('packing.errorLoad')), true);
    }
  }, [onMessage, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (call: () => Promise<unknown>, after?: () => void) => {
    try {
      await call();
      after?.();
      onMessage(t('packing.saved'));
      await load();
    } catch (e: any) {
      onMessage(errorMessage(e, t('packing.errorSave')), true);
    }
  };

  const move = (index: number, delta: number) => {
    const ids = reasons.map((r) => r.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    void run(() => reorderDefectReasons(ids));
  };

  return (
    <>
      <p style={{ margin: '0 0 12px', color: '#475569', fontSize: 14 }}>{t('packing.reasons.hint')}</p>
      <form
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) void run(() => saveDefectReason({ name }), () => setName(''));
        }}
      >
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={255}
          placeholder={t('packing.reasons.namePlaceholder')}
          aria-label={t('packing.reasons.name')}
          style={{ ...input, flex: '1 1 260px', maxWidth: 480 }}
        />
        <button type="submit" style={btn('primary')} disabled={!name.trim()}>+ {t('packing.reasons.add')}</button>
      </form>
      {reasons.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>{t('packing.reasons.empty')}</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
            <thead>
              <tr>
                <th style={{ ...th, width: 90 }}>{t('packing.reasons.order')}</th>
                <th style={th}>{t('packing.reasons.name')}</th>
                <th style={{ ...th, width: 1 }}>{t('packing.reasons.status')}</th>
                <th style={{ ...th, width: 1 }}><span style={visuallyHidden}>{t('packing.col.actions')}</span></th>
              </tr>
            </thead>
            <tbody>
              {reasons.map((r, i) => (
                <tr key={r.id} style={{ background: r.is_active ? '#fff' : '#f8fafc' }}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button type="button" style={{ ...btn('ghost', true), opacity: i === 0 ? 0.35 : 1 }} disabled={i === 0} onClick={() => move(i, -1)} aria-label={t('packing.reasons.up')}>↑</button>{' '}
                    <button type="button" style={{ ...btn('ghost', true), opacity: i === reasons.length - 1 ? 0.35 : 1 }} disabled={i === reasons.length - 1} onClick={() => move(i, 1)} aria-label={t('packing.reasons.down')}>↓</button>
                  </td>
                  <td style={{ ...td, fontSize: 15, fontWeight: 600, color: r.is_active ? '#1e293b' : '#94a3b8' }}>
                    {editing?.id === r.id ? (
                      <form
                        style={{ display: 'flex', gap: 6 }}
                        onSubmit={(e) => {
                          e.preventDefault();
                          void run(() => saveDefectReason({ name: editing.name }, r.id), () => setEditing(null));
                        }}
                      >
                        <input autoFocus value={editing.name} maxLength={255} onChange={(e) => setEditing({ id: r.id, name: e.target.value })} style={input} />
                        <button type="submit" style={btn('primary', true)}>{t('packing.modal.save')}</button>
                        <button type="button" style={btn('ghost', true)} onClick={() => setEditing(null)}>{t('packing.modal.cancel')}</button>
                      </form>
                    ) : (
                      r.name
                    )}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600, color: r.is_active ? '#166534' : '#991b1b' }}>
                    {r.is_active ? t('packing.reasons.active') : t('packing.reasons.inactive')}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button type="button" style={btn('ghost', true)} onClick={() => setEditing({ id: r.id, name: r.name })}>{t('packing.reasons.edit')}</button>
                      <button type="button" style={btn(r.is_active ? 'warn' : 'ghost', true)} onClick={() => void run(() => saveDefectReason({ name: r.name, is_active: !r.is_active }, r.id))}>
                        {r.is_active ? t('packing.reasons.deactivate') : t('packing.reasons.activate')}
                      </button>
                      <button
                        type="button"
                        style={btn('danger', true)}
                        onClick={() => {
                          if (window.confirm(t('packing.reasons.confirmDelete', { name: r.name }))) void run(() => deleteDefectReason(r.id));
                        }}
                      >
                        {t('packing.details.delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default PackingDefectReasonsView;
